import type { ComponentProps } from "react";

import {
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
} from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { initials } from "@/lib/shares/format";
import { cn } from "@/lib/utils";
import type { ShareMember } from "@/lib/shares/types";

/**
 * People avatars, built on the shadcn/Base UI `Avatar` exactly as the ReUI
 * avatar examples compose it:
 * - photo first (`AvatarImage`), initials as the fallback;
 * - the light tinted fallback of c-avatar-14 — a soft fill, the same hue on
 *   the text, and the component's `after:` ring tinted to match — picked per
 *   person so the same name always gets the same colour;
 * - the component's size scale (c-avatar-3) plus `xs` for 28px list rows;
 * - `AvatarBadge` for a state dot (c-avatar-6);
 * - `AvatarGroup` + `AvatarGroupCount` for stacks (c-avatar-11 / c-avatar-24).
 */

export type MemberAvatarSize = "xs" | "sm" | "default" | "lg" | "xl";

// c-avatar-14 tones: soft fill + same-hue text + matching ring, readable in
// both themes (700 text on the light tint, 300 text on the dark one).
const TONES = [
  {
    ring: "after:border-sky-500/25",
    fill: "bg-sky-500/10 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  },
  {
    ring: "after:border-violet-500/25",
    fill: "bg-violet-500/10 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
  },
  {
    ring: "after:border-emerald-500/25",
    fill: "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  },
  {
    ring: "after:border-amber-500/30",
    fill: "bg-amber-500/10 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  },
  {
    ring: "after:border-rose-500/25",
    fill: "bg-rose-500/10 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  },
  {
    ring: "after:border-cyan-500/25",
    fill: "bg-cyan-500/10 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300",
  },
  {
    ring: "after:border-indigo-500/25",
    fill: "bg-indigo-500/10 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300",
  },
  {
    ring: "after:border-orange-500/25",
    fill: "bg-orange-500/10 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
  },
] as const;

function toneFor(seed: string) {
  let hash = 0;

  for (const char of seed) hash = (hash * 31 + char.charCodeAt(0)) | 0;

  return TONES[Math.abs(hash) % TONES.length];
}

// `xs` rides the component's `sm` variant (so AvatarBadge / AvatarGroupCount
// pick the small sizes) and shrinks the box to 20px for dense rows.
const SIZE = {
  xs: { variant: "sm", box: "size-5", text: "text-[0.625rem]" },
  sm: { variant: "sm", box: "", text: "text-caption" },
  default: { variant: "default", box: "", text: "text-xs" },
  lg: { variant: "lg", box: "", text: "text-sm" },
  // Profile photo (c-avatar-5 custom size): 64px, riding the `lg` variant.
  xl: { variant: "lg", box: "size-16", text: "text-lg" },
} as const satisfies Record<
  MemberAvatarSize,
  { variant: "sm" | "default" | "lg"; box: string; text: string }
>;

export function MemberAvatar({
  member,
  size = "sm",
  tinted = true,
  status,
  className,
  ...props
}: Omit<ComponentProps<typeof Avatar>, "size" | "children"> & {
  member: ShareMember;
  size?: MemberAvatarSize;
  /** Per-person colour (default); false keeps the neutral muted fallback. */
  tinted?: boolean;
  /** State dot (AvatarBadge): a pending invitation, or someone on the page right now. */
  status?: "pending" | "online";
}) {
  const scale = SIZE[size];
  const tone = tinted ? toneFor(member.email || member.name) : null;

  return (
    <Avatar size={scale.variant} className={cn(scale.box, tone?.ring, className)} {...props}>
      {member.avatarUrl ? <AvatarImage src={member.avatarUrl} alt="" /> : null}
      <AvatarFallback className={cn("font-medium", scale.text, tone?.fill)}>
        {initials(member.name)}
      </AvatarFallback>
      {status === "pending" && <AvatarBadge className="bg-warning" />}
      {status === "online" && <AvatarBadge className="bg-success" />}
    </Avatar>
  );
}

/**
 * Overlapping stack (AvatarGroup) with a "+N" count, tooltips naming each
 * person on hover (c-avatar-24). Pass `tooltips={false}` inside another
 * hover surface (e.g. a hover-card trigger), where the card lists the names.
 */
export function MemberAvatarGroup({
  members,
  max = 3,
  size = "sm",
  tooltips = true,
  className,
}: {
  members: Array<ShareMember & { pending?: boolean; online?: boolean }>;
  max?: number;
  size?: MemberAvatarSize;
  tooltips?: boolean;
  className?: string;
}) {
  const shown = members.slice(0, max);
  const rest = members.length - shown.length;
  const dense = size === "xs";

  return (
    <AvatarGroup className={cn(dense && "-space-x-1.5", className)}>
      {shown.map((member) => {
        // The avatar is the group's direct child so AvatarGroup's ring and
        // overlap apply; the tooltip trigger renders *as* the avatar.
        const avatar = (
          <MemberAvatar
            key={member.id}
            member={member}
            size={size}
            status={member.pending ? "pending" : member.online ? "online" : undefined}
          />
        );

        if (!tooltips) return avatar;

        return (
          <Tooltip key={member.id}>
            <TooltipTrigger render={avatar} />
            <TooltipContent>
              {member.name}
              {member.pending ? " · invitation en attente" : ""}
              {member.online ? " · en ligne" : ""}
            </TooltipContent>
          </Tooltip>
        );
      })}
      {rest > 0 && (
        <AvatarGroupCount
          className={cn("tabular-nums", dense ? "size-5 text-[0.625rem]" : "text-xs")}
        >
          +{rest}
        </AvatarGroupCount>
      )}
    </AvatarGroup>
  );
}
